import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { Group, Button, TextInput } from "@mantine/core";
import { LIST_WAVES, CREATE_WAVE } from "../queries/waves";
import { formatDateSGT, formatTimeSGT } from "./dateFormat";

const WAVES_PER_PAGE = 10;
const STATUS_ORDER = ["scheduled", "running", "ended"];

// Epoch-ms for sorting; start_date arrives as an ISO string with +08:00 offset
// (or null for a wave with no start yet). Null sorts as oldest.
const startEpoch = (d) => {
    if (!d) return 0;
    const t = Date.parse(d);
    return Number.isNaN(t) ? 0 : t;
};

const DARK_PATTERNS = [
    "sneaking", "interface_interference", "social_engineering", "nagging", "obstruction"
];

// Phase durations are still date-derived (no manual advance) — only the
// offsets are configurable now, so short test waves don't need real days to
// pass. Standard preset reproduces the backend's own defaults (14/14/42 days).
const DURATION_UNITS = [
    { value: "minutes", label: "Minutes", seconds: 60 },
    { value: "hours", label: "Hours", seconds: 3600 },
    { value: "days", label: "Days", seconds: 86400 },
];
const unitValueToSeconds = (value, unit) => {
    const def = DURATION_UNITS.find(u => u.value === unit) || DURATION_UNITS[2];
    return Math.round(Number(value) * def.seconds);
};

// "Now" in SGT, formatted for a <input type="datetime-local"> min attribute —
// stops a past startDate from being pickable at all. Computed from UTC+8
// directly (SGT has no DST) rather than the browser's own timezone, since
// the input's value is always interpreted as SGT regardless of where the
// admin's browser physically is (see the startDate comment in the form below).
const nowSGTForInput = () => {
    const sgt = new Date(Date.now() + 8 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${sgt.getUTCFullYear()}-${pad(sgt.getUTCMonth() + 1)}-${pad(sgt.getUTCDate())}T${pad(sgt.getUTCHours())}:${pad(sgt.getUTCMinutes())}`;
};
const DEFAULT_PHASE_DURATIONS = {
    phase1: { value: 14, unit: "days" },
    phase2: { value: 14, unit: "days" },
    phase3: { value: 42, unit: "days" },
};
const PHASE_FIELDS = [
    { key: "phase1", label: "Phase 1" },
    { key: "phase2", label: "Phase 2" },
    { key: "phase3", label: "Phase 3" },
];

const DURATION_MODE_OPTIONS = [
    { mode: "preset", label: "Standard (14 / 14 / 42 days)" },
    { mode: "custom", label: "Custom" },
];

// status → badge colour
const STATUS_STYLE = {
    scheduled: { background: "#e2e8f0", color: "#475569" },
    running: { background: "#dcfce7", color: "#166534" },
    ended: { background: "#f1f5f9", color: "#94a3b8" },
};

function StatusBadge({ status }) {
    const s = STATUS_STYLE[status] || STATUS_STYLE.scheduled;
    return (
        <span style={{ ...s, borderRadius: 4, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
            {status}
        </span>
    );
}


export default function WaveListPage() {
    const { loading, error, data, refetch } = useQuery(LIST_WAVES);
    const [showModal, setShowModal] = useState(false);
    // startDate is SGT local time with no offset — matches an <input type="datetime-local"> value.
    const [form, setForm] = useState({
        name: "", darkPattern: "sneaking", startDate: "",
    });
    const [durationMode, setDurationMode] = useState("preset"); // "preset" | "custom"
    const [phaseDurations, setPhaseDurations] = useState(DEFAULT_PHASE_DURATIONS);
    // Recomputed each time the modal opens, not on every render — a `min` a few
    // minutes stale from a long-open modal is fine, this just needs to block
    // picking a date that was already in the past when the admin started.
    // showModal is a deliberate recompute trigger; nowSGTForInput() doesn't read it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const minStartDate = useMemo(() => nowSGTForInput(), [showModal]);

    // Sliding pill behind the Standard/Custom segmented control. The two options
    // are different widths, so the slide target (left/width) is measured off the
    // actual buttons rather than assumed, then animated via CSS transition.
    const durationBtnRefs = useRef({});
    const [durationSlider, setDurationSlider] = useState({ left: 0, width: 0 });
    useEffect(() => {
        const el = durationBtnRefs.current[durationMode];
        if (el) setDurationSlider({ left: el.offsetLeft, width: el.offsetWidth });
    }, [durationMode, showModal]);
    const [createWave, { loading: creating, error: createError }] = useMutation(CREATE_WAVE, {
        onCompleted: () => { setShowModal(false); refetch(); }
    });

    // --- search / filter / sort / pagination (mirrors the trade-tab order table) ----
    const [search, setSearch] = useState("");           // by wave id or name
    const [sortDir, setSortDir] = useState("desc");     // by start date
    const [filterPattern, setFilterPattern] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const waves = useMemo(() => data?.listWaves ?? [], [data]);

    // Filter options derived from the data actually present.
    const patternOptions = useMemo(
        () => [...new Set(waves.map((w) => w.dark_pattern))].sort(),
        [waves]
    );
    const statusOptions = useMemo(
        () => STATUS_ORDER.filter((s) => waves.some((w) => w.status === s)),
        [waves]
    );

    const filteredSorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        return waves
            // Search by id or name — applied alongside the pattern/status
            // filters so it narrows within whatever filter is active.
            .filter((w) => {
                if (!q) return true;
                return String(w.wave_id).includes(q) || (w.name || "").toLowerCase().includes(q);
            })
            .filter((w) => !filterPattern || w.dark_pattern === filterPattern)
            .filter((w) => !filterStatus || w.status === filterStatus)
            .slice()
            .sort((a, b) => {
                const tA = startEpoch(a.start_date);
                const tB = startEpoch(b.start_date);
                return sortDir === "asc" ? tA - tB : tB - tA;
            });
    }, [waves, search, filterPattern, filterStatus, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filteredSorted.length / WAVES_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);
    const displayData = filteredSorted.slice((safePage - 1) * WAVES_PER_PAGE, safePage * WAVES_PER_PAGE);

    // Any filter/sort change returns to the first page (order-table behaviour).
    const handleSearch = (v) => { setSearch(v); setCurrentPage(1); };
    const handleSortDir = (dir) => { setSortDir(dir); setCurrentPage(1); };
    const handleFilterPattern = (p) => { setFilterPattern(p); setCurrentPage(1); };
    const handleFilterStatus = (s) => { setFilterStatus(s); setCurrentPage(1); };

    const submit = (e) => {
        e.preventDefault();
        const variables = {
            name: form.name,
            darkPattern: form.darkPattern,
            // datetime-local yields "2026-08-01T09:00" — backend treats it as SGT.
            startDate: form.startDate,
        };
        // Leave the three duration vars unset on "preset" — the backend already
        // defaults to 14/14/42 days when they're omitted, so there's no need to
        // send that split explicitly.
        if (durationMode === "custom") {
            variables.phase1DurationSeconds = unitValueToSeconds(phaseDurations.phase1.value, phaseDurations.phase1.unit);
            variables.phase2DurationSeconds = unitValueToSeconds(phaseDurations.phase2.value, phaseDurations.phase2.unit);
            variables.phase3DurationSeconds = unitValueToSeconds(phaseDurations.phase3.value, phaseDurations.phase3.unit);
        }
        createWave({ variables });
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h1>Waves</h1>
                <button className="admin-btn admin-btn-primary" onClick={() => setShowModal(true)}>+ New Wave</button>
            </div>
            {loading && <p>Loading…</p>}
            {error && <p>Error: {error.message}</p>}
            {data && data.listWaves && (
              <div className="admin-section">
                {waves.length > 0 && (
                    <Group spacing={12} mb={10} style={{ flexWrap: "wrap" }}>
                        <TextInput
                            size="xs"
                            placeholder="Search id or name…"
                            value={search}
                            onChange={(e) => handleSearch(e.currentTarget.value)}
                            rightSection={search
                                ? <Button size="xs" compact variant="subtle" onClick={() => handleSearch("")} tabIndex={-1}>✕</Button>
                                : null}
                            style={{ width: 220 }}
                        />
                        <Group spacing={4}>
                            <span style={{ fontSize: 12, color: "#888" }}>Start:</span>
                            <Button size="xs" compact variant={sortDir === "desc" ? "filled" : "subtle"} onClick={() => handleSortDir("desc")}>↓ Newest</Button>
                            <Button size="xs" compact variant={sortDir === "asc" ? "filled" : "subtle"} onClick={() => handleSortDir("asc")}>↑ Oldest</Button>
                        </Group>
                        <Group spacing={4}>
                            <span style={{ fontSize: 12, color: "#888" }}>Pattern:</span>
                            <Button size="xs" compact variant={filterPattern === "" ? "filled" : "subtle"} onClick={() => handleFilterPattern("")}>All</Button>
                            {patternOptions.map((p) => (
                                <Button key={p} size="xs" compact variant={filterPattern === p ? "filled" : "subtle"} onClick={() => handleFilterPattern(p)}>
                                    {p.replace(/_/g, " ")}
                                </Button>
                            ))}
                        </Group>
                        <Group spacing={4}>
                            <span style={{ fontSize: 12, color: "#888" }}>Status:</span>
                            <Button size="xs" compact variant={filterStatus === "" ? "filled" : "subtle"} onClick={() => handleFilterStatus("")}>All</Button>
                            {statusOptions.map((s) => (
                                <Button key={s} size="xs" compact variant={filterStatus === s ? "filled" : "subtle"} onClick={() => handleFilterStatus(s)}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                </Button>
                            ))}
                        </Group>
                    </Group>
                )}
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Wave ID</th><th>Name</th><th>Dark Pattern</th><th>Status</th>
                            <th>Phase</th><th>Start Date</th><th>Start Time</th>
                            <th>End Date</th><th>End Time</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {waves.length === 0 && (
                            <tr><td colSpan={10} style={{ color: "#888", textAlign: "center" }}>No waves yet.</td></tr>
                        )}
                        {waves.length > 0 && displayData.length === 0 && (
                            <tr><td colSpan={10} style={{ color: "#888", textAlign: "center" }}>No waves match the selected filters.</td></tr>
                        )}
                        {displayData.map(w => (
                            <tr key={w.wave_id}>
                                <td>{w.wave_id}</td>
                                <td>{w.name || "-"}</td>
                                <td>{w.dark_pattern}</td>
                                <td><StatusBadge status={w.status} /></td>
                                <td>{w.current_phase}</td>
                                <td>{formatDateSGT(w.start_date)}</td>
                                <td>{formatTimeSGT(w.start_date)}</td>
                                <td>{formatDateSGT(w.end_date)}</td>
                                <td>{formatTimeSGT(w.end_date)}</td>
                                <td><Link to={`/admin/waves/${w.wave_id}`}>Open</Link></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {totalPages > 1 && (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 8 }}>
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "4px 10px", cursor: "pointer" }}>Previous</button>
                        <span>Page {safePage} / {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "4px 10px", cursor: "pointer" }}>Next</button>
                    </div>
                )}
              </div>
            )}

            {showModal && (
                <div className="admin-modal-bg" onClick={() => setShowModal(false)}>
                    <form className="admin-modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
                        <h3>Create wave</h3>
                        <label>Name</label>
                        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                               placeholder="e.g. Obstruction Study" required />
                        <label>Dark pattern</label>
                        <select value={form.darkPattern} onChange={e => setForm({ ...form, darkPattern: e.target.value })}>
                            {DARK_PATTERNS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <label>Start date (SGT)</label>
                        <input type="datetime-local" value={form.startDate} min={minStartDate}
                               onChange={e => setForm({ ...form, startDate: e.target.value })} required />

                        <label>Phase durations</label>
                        {/* Segmented control: the whole bar is grey, the selected option
                            pops out as a blue pill that slides horizontally to the clicked
                            option — unselected sits flush in the grey, no background of its own. */}
                        <div
                            style={{
                                position: "relative", display: "inline-flex", gap: 3, padding: 3,
                                marginBottom: 6, background: "#e9ecef", borderRadius: 6,
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute", top: 3, bottom: 3,
                                    left: durationSlider.left, width: durationSlider.width,
                                    background: "#3b5bdb", borderRadius: 4,
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                    transition: "left 180ms ease, width 180ms ease",
                                }}
                            />
                            {DURATION_MODE_OPTIONS.map(({ mode, label }) => {
                                const active = durationMode === mode;
                                return (
                                    <button
                                        key={mode}
                                        ref={el => { durationBtnRefs.current[mode] = el; }}
                                        type="button"
                                        onClick={() => setDurationMode(mode)}
                                        style={{
                                            position: "relative", zIndex: 1,
                                            border: "none", borderRadius: 4, cursor: "pointer",
                                            background: "transparent",
                                            padding: "5px 12px", fontSize: 13, fontWeight: 600,
                                            color: active ? "#fff" : "#868e96",
                                            transition: "color 180ms ease",
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Always visible so the standard split is legible, not just implied by
                            the button label — locked (disabled) unless Custom is selected. In
                            preset mode these show the fixed 14/14/42 defaults regardless of
                            whatever was last typed into them under Custom. */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                            {PHASE_FIELDS.map(({ key, label }) => {
                                const locked = durationMode === "preset";
                                const shown = locked ? DEFAULT_PHASE_DURATIONS[key] : phaseDurations[key];
                                return (
                                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ width: 56, flexShrink: 0, whiteSpace: "nowrap", fontSize: 13, color: "#555" }}>{label}</span>
                                        <input
                                            type="number" min="1" step="any" required
                                            disabled={locked}
                                            value={shown.value}
                                            onChange={e => setPhaseDurations(pd => ({
                                                ...pd, [key]: { ...pd[key], value: e.target.value }
                                            }))}
                                            style={{ width: 80 }}
                                        />
                                        <select
                                            disabled={locked}
                                            value={shown.unit}
                                            onChange={e => setPhaseDurations(pd => ({
                                                ...pd, [key]: { ...pd[key], unit: e.target.value }
                                            }))}
                                        >
                                            {DURATION_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                                        </select>
                                    </div>
                                );
                            })}
                            {/* Always rendered (not just in Custom) so the modal doesn't resize
                                on mode switch — invisible but still occupying its line in Standard. */}
                            <span style={{ fontSize: 12, color: "#888", visibility: durationMode === "custom" ? "visible" : "hidden" }}>
                                For a quick test run, try e.g. 5 / 5 / 10 minutes.
                            </span>
                        </div>
                        {createError && <p style={{ color: "crimson", fontSize: 13 }}>{createError.message}</p>}
                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <button type="button" className="admin-btn" onClick={() => setShowModal(false)}>Cancel</button>
                            <button type="submit" className="admin-btn admin-btn-primary" disabled={creating}>
                                {creating ? "Creating…" : "Create"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
